/**
 * Offline copy of the user's repo in the app's own file storage (iOS app only).
 *
 * Every file and note in the repo is mirrored into the app's Library/NoCloud folder (not purged
 * under storage pressure, included in device backups, not shown in the Files app). Files
 * downloaded from the Solo 2 are stored there too, marked pending, and uploaded by the next
 * sync; a copy also goes to the Documents folder, which the Files app shows under
 * "On My iPhone > Mala Racing". Offline, the repo list is served from the mirror. The website
 * never uses this: there is no app folder to mirror into.
 */
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Network } from '@capacitor/network'
import { isNativeApp } from './native'
import { fromBase64, toBase64 } from '../solo2/bridge'
import {
  deleteSession,
  downloadSession,
  listSessions,
  loadMeta,
  loadNotes,
  mergeMeta,
  saveNotes,
  uploadSession,
  type Notes,
  type SessionInfo,
  type SessionInfos,
  type StoredSession,
} from './storage'

const DIR = Directory.Library
const ROOT = 'NoCloud/repo'
const INDEX = `${ROOT}/_index.json`
/** An earlier build kept the mirror under Documents; it is removed on the first sync. */
const LEGACY_ROOT = 'repo'
const SETTING_KEY = 'mala.offline.v1'

export interface CachedFile {
  name: string
  /** Repo key once uploaded; absent while pending */
  key?: string
  size: number
  /** ISO time of the repo object, for change detection */
  lastModified: string
  /** Downloaded from the logger, not yet in the repo */
  pending?: boolean
}

export interface OfflineIndex {
  files: Record<string, CachedFile>
  notes: Notes
  /** Per-file info (date, laps, best, track), mirrored from the repo and set for queued files */
  meta?: SessionInfos
  /** Notes edited offline that still need to be pushed */
  notesDirty?: boolean
  syncedAt?: string
}

export interface SyncStatus {
  state: 'idle' | 'syncing' | 'offline' | 'error'
  syncedAt?: string
  fileCount: number
  bytes: number
  pending: number
  error?: string
}

export const offlineAvailable = isNativeApp

export function offlineEnabled(): boolean {
  try {
    return localStorage.getItem(SETTING_KEY) === '1'
  } catch {
    return false
  }
}

export function setOfflineEnabled(on: boolean): void {
  try {
    localStorage.setItem(SETTING_KEY, on ? '1' : '0')
  } catch {
    /* storage may be unavailable */
  }
}

// ---------- files ----------

const EMPTY: OfflineIndex = { files: {}, notes: {} }

/** All index read-modify-write sequences go through this chain so a queue add cannot be lost
 *  under a running sync (both used to read the index, then write their own copy back). */
let chain: Promise<unknown> = Promise.resolve()
export function withIndex<T>(fn: () => Promise<T>): Promise<T> {
  const run = chain.then(fn, fn)
  chain = run.catch(() => undefined)
  return run
}

export async function readIndex(): Promise<OfflineIndex> {
  try {
    const r = await Filesystem.readFile({ path: INDEX, directory: DIR })
    const text = typeof r.data === 'string' ? new TextDecoder().decode(fromBase64(r.data)) : await r.data.text()
    const parsed = JSON.parse(text) as OfflineIndex
    return { files: parsed.files ?? {}, notes: parsed.notes ?? {}, meta: parsed.meta ?? {}, notesDirty: parsed.notesDirty, syncedAt: parsed.syncedAt }
  } catch {
    return { ...EMPTY, files: {}, notes: {} }
  }
}

async function writeIndex(index: OfflineIndex): Promise<void> {
  await Filesystem.writeFile({
    path: INDEX,
    directory: DIR,
    data: toBase64(new TextEncoder().encode(JSON.stringify(index))),
    recursive: true,
  })
}

export async function saveCachedFile(name: string, bytes: ArrayBuffer, meta: Omit<CachedFile, 'name' | 'size'>, info?: SessionInfo): Promise<void> {
  await Filesystem.writeFile({ path: `${ROOT}/${name}`, directory: DIR, data: toBase64(new Uint8Array(bytes)), recursive: true })
  await withIndex(async () => {
    const index = await readIndex()
    index.files[name] = { name, size: bytes.byteLength, ...meta }
    if (info) index.meta = { ...(index.meta ?? {}), [name]: info }
    await writeIndex(index)
  })
}

/** Keep a file's info (date, laps, best lap, track) for a session that lives only on this phone. */
export async function rememberInfo(name: string, info: SessionInfo): Promise<void> {
  await withIndex(async () => {
    const index = await readIndex()
    index.meta = { ...(index.meta ?? {}), [name]: info }
    await writeIndex(index)
  })
}

export async function readCachedFile(name: string): Promise<ArrayBuffer | null> {
  try {
    const r = await Filesystem.readFile({ path: `${ROOT}/${name}`, directory: DIR })
    if (typeof r.data === 'string') {
      const u = fromBase64(r.data)
      return u.buffer.slice(u.byteOffset, u.byteOffset + u.byteLength) as ArrayBuffer
    }
    return await r.data.arrayBuffer()
  } catch {
    return null
  }
}

export async function deleteCachedFile(name: string): Promise<void> {
  await Filesystem.deleteFile({ path: `${ROOT}/${name}`, directory: DIR }).catch(() => undefined)
  await withIndex(async () => {
    const index = await readIndex()
    delete index.files[name]
    await writeIndex(index)
  })
}

/** Remove the whole mirror (account deletion, sign-out). */
export async function clearOffline(): Promise<void> {
  await Filesystem.rmdir({ path: ROOT, directory: DIR, recursive: true }).catch(() => undefined)
  await Filesystem.rmdir({ path: LEGACY_ROOT, directory: Directory.Data, recursive: true }).catch(() => undefined)
}

/** A copy the user can see and share: Files app, "On My iPhone > Mala Racing". */
export async function exportToFiles(name: string, bytes: ArrayBuffer): Promise<void> {
  await Filesystem.writeFile({ path: name, directory: Directory.Documents, data: toBase64(new Uint8Array(bytes)), recursive: true })
}

/** Queue a logger download for the repo: mirrored now, uploaded by the next sync. */
export async function queueForUpload(name: string, bytes: ArrayBuffer, info?: SessionInfo): Promise<void> {
  await saveCachedFile(name, bytes, { pending: true, lastModified: new Date().toISOString() }, info)
}

// ---------- The Files-app folder (Documents): sessions the user can see and share ----------

export interface DocumentFile {
  name: string
  size: number
  mtime: number
}

export async function listDocuments(): Promise<DocumentFile[]> {
  try {
    const { files } = await Filesystem.readdir({ directory: Directory.Documents, path: '' })
    return files
      .filter((f) => f.type === 'file' && /\.(xrk|xrz|hrz)$/i.test(f.name))
      .map((f) => ({ name: f.name, size: f.size, mtime: f.mtime }))
      .sort((a, b) => b.mtime - a.mtime)
  } catch {
    return []
  }
}

export async function readDocument(name: string): Promise<ArrayBuffer | null> {
  try {
    const r = await Filesystem.readFile({ path: name, directory: Directory.Documents })
    if (typeof r.data === 'string') {
      const u = fromBase64(r.data)
      return u.buffer.slice(u.byteOffset, u.byteOffset + u.byteLength) as ArrayBuffer
    }
    return await r.data.arrayBuffer()
  } catch {
    return null
  }
}

export async function deleteDocument(name: string): Promise<void> {
  await Filesystem.deleteFile({ path: name, directory: Directory.Documents }).catch(() => undefined)
}

/** Record a note edit; pushed by the next sync. */
export async function saveNoteOffline(notes: Notes): Promise<void> {
  await withIndex(async () => {
    const index = await readIndex()
    index.notes = notes
    index.notesDirty = true
    await writeIndex(index)
  })
}

/** The repo list as the mirror knows it, for use when the network is gone. */
export function cachedAsStored(index: OfflineIndex): StoredSession[] {
  return Object.values(index.files)
    .filter((f) => f.key)
    .map((f) => ({ key: f.key!, name: f.name, size: f.size, lastModified: new Date(f.lastModified) }))
    .sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime())
}

export function statusOf(index: OfflineIndex, state: SyncStatus['state'] = 'idle', error?: string): SyncStatus {
  const files = Object.values(index.files)
  return {
    state,
    syncedAt: index.syncedAt,
    fileCount: files.length,
    bytes: files.reduce((a, f) => a + f.size, 0),
    pending: files.filter((f) => f.pending).length,
    error,
  }
}

// ---------- sync ----------

export interface SyncPlan {
  download: StoredSession[]
  upload: CachedFile[]
  remove: string[]
}

/** What a sync has to do, given the mirror and the live repo list. Pure, for tests. */
export function planSync(index: OfflineIndex, cloud: StoredSession[]): SyncPlan {
  const byName = new Map(cloud.map((c) => [c.name, c]))
  const download = cloud.filter((c) => {
    const f = index.files[c.name]
    return !f || f.pending || f.size !== c.size || f.lastModified !== c.lastModified.toISOString()
  })
  const upload = Object.values(index.files).filter((f) => f.pending && !byName.has(f.name))
  const remove = Object.values(index.files)
    .filter((f) => !f.pending && !byName.has(f.name))
    .map((f) => f.name)
  return { download, upload, remove }
}

export async function isOnline(): Promise<boolean> {
  try {
    const s = await Network.getStatus()
    return s.connected && s.connectionType !== 'none'
  } catch {
    return typeof navigator === 'undefined' ? true : navigator.onLine
  }
}

let syncing: Promise<SyncStatus> | null = null

/**
 * Mirror the repo: push pending files and notes, pull new or changed files, drop files deleted
 * elsewhere. Resolves with the status; never throws. Concurrent calls share one run.
 */
export function syncRepo(onProgress?: (s: SyncStatus) => void): Promise<SyncStatus> {
  if (syncing) return syncing
  syncing = (async () => {
    // Files queued while a pass runs are picked up by another pass.
    let status = await syncPass(onProgress)
    for (let i = 0; i < 5 && status.state === 'idle' && status.pending > 0; i++) status = await syncPass(onProgress)
    return status
  })().finally(() => {
    syncing = null
  })
  return syncing
}

async function syncPass(onProgress?: (s: SyncStatus) => void): Promise<SyncStatus> {
  {
    await Filesystem.rmdir({ path: LEGACY_ROOT, directory: Directory.Data, recursive: true }).catch(() => undefined)
    let index = await readIndex()
    onProgress?.(statusOf(index, 'syncing'))
    let cloud: StoredSession[]
    let notes: Notes
    try {
      ;[cloud, notes] = await Promise.all([listSessions(), loadNotes()])
    } catch (e) {
      return statusOf(index, 'offline', e instanceof Error ? e.message : String(e))
    }
    try {
      // Notes: offline edits win over the repo copy, then the merged set is pushed.
      if (index.notesDirty) {
        notes = { ...notes, ...index.notes }
        await saveNotes(notes)
        index.notesDirty = false
      }
      index.notes = notes

      const plan = planSync(index, cloud)
      const uploadedInfo: SessionInfos = {}
      for (const f of plan.upload) {
        const bytes = await readCachedFile(f.name)
        if (!bytes) {
          delete index.files[f.name]
          continue
        }
        const key = await uploadSession(new File([bytes], f.name))
        if (index.meta?.[f.name]) uploadedInfo[f.name] = index.meta[f.name]
        // Re-read under the lock: another file may have been queued meanwhile.
        index = await withIndex(async () => {
          const latest = await readIndex()
          latest.files[f.name] = { ...(latest.files[f.name] ?? f), key, pending: false, lastModified: new Date().toISOString() }
          await writeIndex(latest)
          return latest
        })
        onProgress?.(statusOf(index, 'syncing'))
      }
      if (plan.upload.length) cloud = await listSessions()
      // Per-file info: push what the queue knew, then mirror the repo's copy. Info for files
      // that are only on this phone stays, the repo's wins for files it knows.
      const repoMeta = Object.keys(uploadedInfo).length ? await mergeMeta(uploadedInfo) : await loadMeta()
      index.meta = { ...(index.meta ?? {}), ...repoMeta }

      for (const c of planSync(index, cloud).download) {
        const bytes = await downloadSession(c.key)
        await Filesystem.writeFile({ path: `${ROOT}/${c.name}`, directory: DIR, data: toBase64(new Uint8Array(bytes)), recursive: true })
        index = await withIndex(async () => {
          const latest = await readIndex()
          latest.files[c.name] = { name: c.name, key: c.key, size: c.size, lastModified: c.lastModified.toISOString() }
          await writeIndex(latest)
          return latest
        })
        onProgress?.(statusOf(index, 'syncing'))
      }
      const meta = index.meta
      index = await withIndex(async () => {
        const latest = await readIndex()
        for (const name of planSync(latest, cloud).remove) {
          await Filesystem.deleteFile({ path: `${ROOT}/${name}`, directory: DIR }).catch(() => undefined)
          delete latest.files[name]
        }
        // Keys can change when a file is re-uploaded; keep them current.
        for (const c of cloud) if (latest.files[c.name]) latest.files[c.name].key = c.key
        latest.meta = meta
        latest.syncedAt = new Date().toISOString()
        await writeIndex(latest)
        return latest
      })
      return statusOf(index, 'idle')
    } catch (e) {
      index = await readIndex()
      return statusOf(index, 'error', e instanceof Error ? e.message : String(e))
    }
  }
}

/** Delete from the repo and from the mirror together. */
export async function deleteEverywhere(item: StoredSession): Promise<void> {
  await deleteSession(item.key)
  await deleteCachedFile(item.name)
}
